import * as React from 'react';
import { http, Field } from '../api';
import { LookupFieldInput } from './LookupFieldInput';
import { DateInput } from './DateInput';
import './FolderCrudModal.css';

type Mode = 'create-root' | 'create-child' | 'edit' | 'delete';

export interface FolderCrudModalProps {
  open: boolean;
  mode: Mode;
  drawerId: number;
  parentFolderId?: number | null;
  folder?: { id: number; name: string } | null;
  drawerFields?: Field[];
  onClose: () => void;
  onSaved: (saved?: { id: number; name: string }) => void;
}

export function FolderCrudModal(props: FolderCrudModalProps) {
  const { open, mode, drawerId, parentFolderId = null, folder, drawerFields = [], onClose, onSaved } = props;

  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [folderFields, setFolderFields] = React.useState<Field[]>([]);
  const [folderValues, setFolderValues] = React.useState<Record<string, any>>({});
  const [loadingFolder, setLoadingFolder] = React.useState(false);
  const [loadedFolderParentId, setLoadedFolderParentId] = React.useState<number | null>(null);

  // Only root folders (folderParentId = -1) have folder field values UI.
  // Prefer the real value from the loaded folder (edit/delete). Fallback to prop for create flows.
  const effectiveParentId = (mode === 'edit' || mode === 'delete') ? loadedFolderParentId : parentFolderId;
  const isRootParent = effectiveParentId === -1;
  const needsFolderValues = isRootParent && (mode === 'create-root' || mode === 'edit');

  React.useEffect(() => {
    if (!open) return;

    setError(null);
    setLoadedFolderParentId(null);

    // Subfolder create/edit uses only a name/description
    if (mode === 'edit' && folder) setName(folder.name ?? '');
    if (mode === 'create-root' || mode === 'create-child') setName('');
    if (mode === 'delete') setName(folder?.name ?? '');
  }, [open, mode, folder]);

  // Load the folder itself on edit/delete so we can reliably determine folderParentId.
  React.useEffect(() => {
    if (!open) return;
    if (mode !== 'edit' && mode !== 'delete') return;
    if (!folder) return;

    let cancelled = false;
    (async () => {
      try {
        const f: any = await http(`/api/folder/${folder.id}`);
        if (cancelled) return;
        const p = f?.folderParentId ?? f?.FolderParentId;
        setLoadedFolderParentId(typeof p === 'number' ? p : (p == null ? null : Number(p)));

        // also refresh name to exact backend value when available
        const desc = f?.folderDescription ?? f?.FolderDescription;
        if (typeof desc === 'string') setName(desc);
      } catch {
        if (!cancelled) setLoadedFolderParentId(null);
      }
    })();

    return () => { cancelled = true; };
  }, [open, mode, folder]);

  // Load drawer fields and existing folder values only when we need FolderValues editing (root folder)
  React.useEffect(() => {
    if (!open) return;

    if (!needsFolderValues) {
      setFolderFields([]);
      return;
    }

    const folderDefs = (drawerFields || []).filter(f => !!f.folderfield);
    setFolderFields(folderDefs);
  }, [open, needsFolderValues, drawerFields]);

  React.useEffect(() => {
    if (!open) return;
    if (!needsFolderValues) return;

    if (mode !== 'edit' || !folder) {
      setFolderValues({});
      return;
    }

    let cancelled = false;
    setLoadingFolder(true);

    (async () => {
      try {
        const f: any = await http(`/api/folder/${folder.id}`);
        if (cancelled) return;
        const raw = (f?.folderValues ?? f?.FolderValues ?? {}) as Record<string, any>;

        // Normalize keys to match field names (case-insensitive)
        const normalized: Record<string, any> = {};
        const rawLower: Record<string, any> = {};
        Object.keys(raw || {}).forEach(k => { rawLower[String(k).toLowerCase()] = (raw as any)[k]; });

        // Ensure every folder field gets an entry (so inputs are controlled)
        (folderFields ?? []).forEach((fld: any) => {
          const key = String(fld?.name ?? '');
          if (!key) return;
          const lower = key.toLowerCase();
          if (rawLower[lower] !== undefined) normalized[key] = rawLower[lower];
          else normalized[key] = '';
        });

        // Also keep any extra keys returned by the backend
        Object.keys(raw || {}).forEach(k => {
          const kk = String(k);
          if (normalized[kk] === undefined) normalized[kk] = (raw as any)[k];
        });

        setFolderValues(normalized);
      } catch {
        if (cancelled) return;
        setFolderValues({});
      } finally {
        if (!cancelled) setLoadingFolder(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, needsFolderValues, mode, folder, folderFields]);

  // Also prefill the description-only input when editing a subfolder
  React.useEffect(() => {
    if (!open) return;
    if (needsFolderValues) return;
    if (mode !== 'edit' || !folder) return;
    setName(folder.name ?? '');
  }, [open, needsFolderValues, mode, folder]);

  if (!open) return null;

  const title =
    mode === 'create-root' ? 'Nieuwe folder' :
    mode === 'create-child' ? 'Nieuwe subfolder' :
    mode === 'edit' ? 'Folder bewerken' :
    'Folder verwijderen';

  function renderFolderFieldInput(f: Field) {
    const rawValue = folderValues?.[f.name];

    const set = (v: any) => setFolderValues(prev => ({ ...prev, [f.name]: v }));

    // Lookup field: incremental search dropdown
    if (f.fieldLookupName && f.fieldLookupKeyColumn && f.fieldLookupDisplayColumn) {
      return (
        <LookupFieldInput
          lookupName={f.fieldLookupName}
          keyColumn={f.fieldLookupKeyColumn}
          displayColumn={f.fieldLookupDisplayColumn}
          fieldName={f.name}
          mappings={f.fieldLookupMappings}
          value={String(rawValue ?? '')}
          onSelect={selectedValues => setFolderValues(prev => ({ ...prev, ...selectedValues }))}
          className="input-default"
        />
      );
    }

    const options = f.options;
    if (options && options.length > 0) {
      return (
        <select className="input-default" value={String(rawValue ?? '')} onChange={e => set(e.target.value)}>
          <option value=""></option>
          {options.map((opt: string) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (String(f.datatype).toLowerCase() === 'datetime') {
      const toDateInputValue = (v: any): string => {
        if (v == null || v === '') return '';
        if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
        const s = String(v).trim();
        if (!s) return '';
        // ISO datetime -> yyyy-MM-dd
        if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
        // already yyyy-MM-dd
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        // compact yyyyMMdd or yyyyMMddHHmmss
        if (/^\d{8,14}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        // try fallback parse
        const d = new Date(s);
        if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
        return '';
      };

      // Custom picker so the displayed format follows the configured culture (not the browser).
      return (
        <DateInput
          className="input-default"
          value={toDateInputValue(rawValue)}
          onChange={v => set(v)}
        />
      );
    }

    return (
      <input
        className="input-default"
        value={String(rawValue ?? '')}
        onChange={e => set(e.target.value)}
      />
    );
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (mode === 'delete') {
        if (!folder) throw new Error('Geen folder geselecteerd');
        await http(`/api/folder/${folder.id}`, { method: 'DELETE' });
        onSaved();
        return;
      }

      const trimmed = name.trim();

      // Subfolders: only update description
      if (mode === 'create-child') {
        if (!trimmed) { setError('Omschrijving is verplicht'); return; }

        const createPayload = {
          id: 0,
          drawerId,
          folderParentId: parentFolderId ?? -1,
          folderDescription: trimmed
        };
        const created: any = await http('/api/folder', { method: 'POST', json: createPayload });
        const createdId = Number(created?.id ?? created?.Id);
        onSaved({ id: Number.isFinite(createdId) ? createdId : 0, name: trimmed });
        return;
      }

      if (mode === 'edit' && effectiveParentId != null && effectiveParentId !== -1) {
        if (!folder) throw new Error('Geen folder geselecteerd');
        if (!trimmed) { setError('Omschrijving is verplicht'); return; }

        const payload = {
          id: folder.id,
          drawerId,
          folderParentId: effectiveParentId,
          folderDescription: trimmed
        };
        await http(`/api/folder/${folder.id}`, { method: 'PUT', json: payload });
        onSaved({ id: folder.id, name: trimmed });
        return;
      }

      // Root folders: use FolderValues (like office add-in)
      if (mode === 'create-root') {
        const payload = {
          drawerid: drawerId,
          folderParentId: -1,
          foldervalues: folderValues
        };
        const created: any = await http('/api/folder', { method: 'POST', json: payload as any });
        const createdId = Number(created?.id ?? created?.Id);

        // Reload to get computed sorting/display value
        let displayName = 'Folder';
        try {
          if (Number.isFinite(createdId) && createdId > 0) {
            const f: any = await http(`/api/folder/${createdId}`);
            displayName = String(f?.folderSortingValue ?? f?.FolderSortingValue ?? f?.folderDescription ?? f?.FolderDescription ?? displayName);
          }
        } catch { /* ignore */ }

        onSaved({ id: Number.isFinite(createdId) ? createdId : 0, name: displayName });
        return;
      }

      if (mode === 'edit') {
        if (!folder) throw new Error('Geen folder geselecteerd');

        const payload = {
          id: folder.id,
          drawerId,
          folderParentId: -1,
          folderValues: folderValues
        };
        await http(`/api/folder/${folder.id}`, { method: 'PUT', json: payload });

        // Reload to get computed sorting/display value
        let displayName = folder.name;
        try {
          const f: any = await http(`/api/folder/${folder.id}`);
          displayName = String(f?.folderSortingValue ?? f?.FolderSortingValue ?? f?.folderDescription ?? f?.FolderDescription ?? displayName);
        } catch { /* ignore */ }

        onSaved({ id: folder.id, name: displayName });
        return;
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="folder-modal-overlay" role="dialog" aria-modal="true">
      <div className="folder-modal">
        <div className="folder-modal-header">
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button type="button" className="folder-modal-close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7A1 1 0 0 0 5.7 7.1L10.6 12l-4.9 4.9a1 1 0 1 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4Z" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <div className="folder-modal-body">
          {error && <div className="error-text" style={{ marginTop: 0 }}>{error}</div>}

          {needsFolderValues ? (
            <>
              {loadingFolder ? (
                <div style={{ marginTop: 12 }}>Laden...</div>
              ) : (
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '220px 1fr', gap: 8 }}>
                  {folderFields.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1' }}>
                      Geen folder-velden gedefinieerd voor deze lade (controleer lade-inrichting).
                    </div>
                  ) : (
                    folderFields.map(f => {
                      const title = String(f.title ?? f.name ?? '');
                      const required = Boolean(f.required);
                      const key = String(f.name);
                      return (
                        <React.Fragment key={key}>
                          <label style={{ alignSelf: 'center' }}>{title}{required ? ' *' : ''}</label>
                          {renderFolderFieldInput(f)}
                        </React.Fragment>
                      );
                    })
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', marginBottom: 6 }}>Omschrijving</label>
              <input
                className="input-default"
                disabled={saving || mode === 'delete'}
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
              />
              {mode === 'delete' && (
                <div style={{ marginTop: 10 }}>
                  Verwijderen kan mislukken als er nog subfolders of documenten in deze folder zitten.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="folder-modal-footer">
          <button type="button" className="btn-secondary" disabled={saving} onClick={onClose}>Annuleren</button>
          <button
            type="button"
            className={mode === 'delete' ? 'btn-danger' : 'btn-primary'}
            disabled={saving || (mode !== 'delete' && !needsFolderValues && !name.trim())}
            onClick={save}
          >
            {saving ? 'Bezig...' : (mode === 'delete' ? 'Verwijderen' : 'Opslaan')}
          </button>
        </div>
      </div>
    </div>
  );
}
